// Date axes for per-game scatters (Accuracy, Score through a stage), shared
// so every chart with a date across places a game on the same day.
//
// Where a game's dot goes across the chart: in the column of the calendar
// day it was played, in the reader's own time zone, the same day its label
// and tooltip name. Placing a dot at its exact instant put an evening game
// near the next day's tick, and the labels were read in UTC, so a game could
// sit and be labelled a day away from the date the reader knows it by.
// Games of one day sit side by side inside that day's column, never in a
// neighbour's.

const DAY = 24 * 60 * 60 * 1000;

/** Local midnight of the day `at` falls on. */
export function startOfDay(at: number): number {
  const d = new Date(at);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/** Calendar days from `from`'s day to `at`'s day, in local time (a DST day still counts as one). */
export function dayIndex(at: number, from: number): number {
  return Math.round((startOfDay(at) - startOfDay(from)) / DAY);
}

/** "Sep 14": a day in the reader's own time zone. */
export const dateLabel = (at: number) => new Date(at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

/** The chart's day columns: how many, and where each day's centre sits. */
export interface DayAxis {
  days: number;
  /** Width of one day's column, in chart units. */
  column: number;
  /** The centre of the day `at` falls on. */
  centre(at: number): number;
}

/** Days from `from` to `to`, both included, across `left`…`right`. */
export function dayAxis(from: number, to: number, left: number, right: number): DayAxis {
  const days = Math.max(1, dayIndex(to, from) + 1);
  const column = (right - left) / days;
  return { days, column, centre: (at) => left + (Math.min(days - 1, Math.max(0, dayIndex(at, from))) + 0.5) * column };
}

/**
 * Each point's x offset from its day's centre: games of one day side by
 * side, `spread` apart, narrowed so the group stays inside the day's column
 * (80 % of it).
 */
export function dayOffsets(ats: number[], from: number, column: number, spread: number): number[] {
  const byDay = new Map<number, number[]>();
  ats.forEach((at, i) => {
    const day = dayIndex(at, from);
    byDay.set(day, [...(byDay.get(day) ?? []), i]);
  });
  const out = new Array<number>(ats.length).fill(0);
  for (const indices of byDay.values()) {
    if (indices.length < 2) continue;
    const gap = Math.min(spread, (column * 0.8) / (indices.length - 1));
    indices.forEach((i, k) => (out[i] = (k - (indices.length - 1) / 2) * gap));
  }
  return out;
}
